"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { AlertTriangle, Calculator, Save, ArrowLeft, Loader2, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import { DocumentManager } from "./DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

interface MaterialityRevisionProps {
  planningData: any;
  onRevisionComplete: () => void;
  crossSectionAnswers?: Record<string, string | number | boolean>;
  companyName?: string;
}

export function MaterialityRevision({
  planningData,
  onRevisionComplete,
  crossSectionAnswers = {},
  companyName: propCompanyName,
}: MaterialityRevisionProps) {
  const { isArchivalMode } = useArchivalMode();
  const [companyName, setCompanyName] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);
  
  // Document states for M-15 (Justification)
  const [m15Documents, setM15Documents] = useState<any[]>([]);
  const [m15TextResponse, setM15TextResponse] = useState<any>(null);
  
  const [step, setStep] = useState(1); // 1 = landing page (both questions), 3 = revision form
  const [changesObserved, setChangesObserved] = useState("");
  const [revisionNeeded, setRevisionNeeded] = useState("");
  const [revisionData, setRevisionData] = useState({
    benchmarkData: [] as Array<{
      benchmark: string;
      amount: number;
      adjustments: number;
      factor: number;
    }>,
    determinedMateriality: 0,
    justification: "",
    auditTrail: {
      userId: "Current User",
      timestamp: new Date().toISOString(),
      oldMateriality: 0,
      newMateriality: 0,
    },
  });

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      if (propCompanyName) {
        setCompanyName(propCompanyName);
        return;
      }
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, [propCompanyName]);

  // Load revision data from SharePoint when component mounts
  useEffect(() => {
    const loadFromSharePoint = async () => {
      if (!companyName) return;

      try {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          console.log("Planning API not available for Materiality Revision");
          return;
        }

        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "materiality"
        });

        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;

          // Load M-9 (both questions)
          if (responses['M-9']) {
            const m9Response = responses['M-9'];
            if (m9Response.selectedOption) {
              setChangesObserved(m9Response.selectedOption);
            }
            if (m9Response.subAnswer) {
              setRevisionNeeded(m9Response.subAnswer);
              // If both are "Yes", load revision data but stay on landing page
              if (m9Response.selectedOption === "Yes" && m9Response.subAnswer === "Yes") {
                // Load revision data if available
                if (responses['revisedMaterialityTable']) {
                  const tableData = responses['revisedMaterialityTable'];
                  if (tableData.table && tableData.table.rows) {
                    setRevisionData(prev => ({
                      ...prev,
                      benchmarkData: tableData.table.rows.map((row: any) => ({
                        benchmark: row.benchmark || "",
                        amount: row.amount || 0,
                        adjustments: row.normalizationAdjustments || 0,
                        factor: row.benchmarkFactor || 5
                      }))
                    }));
                  }
                }
                if (responses['revisedMateriality']) {
                  const revised = responses['revisedMateriality'];
                  if (revised.determinedMateriality !== undefined) {
                    setRevisionData(prev => ({
                      ...prev,
                      determinedMateriality: revised.determinedMateriality || 0
                    }));
                  }
                }
              }
            }
          }

          // Load M-15 (Justification for Revision)
          if (responses['M-15']) {
            const m15Response = responses['M-15'];
            if (m15Response.textResponse) {
              setM15TextResponse(m15Response.textResponse);
              setRevisionData(prev => ({
                ...prev,
                justification: m15Response.textResponse.html || m15Response.textResponse.plainText || ''
              }));
            }
            if (m15Response.documents) {
              setM15Documents(m15Response.documents);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load Materiality Revision responses from SharePoint:", error);
      }
    };

    loadFromSharePoint();
  }, [companyName, step]);

  // Note: Revision section is now always accessible, but some features require planning data
  // If planning data is not available, show a message but allow access
  const hasPlanningData = planningData && planningData.determinedMateriality > 0;

  const handleStep1Response = (value: string) => {
    setChangesObserved(value);
    if (value === "No") {
      // Reset revision needed if first question is No
      setRevisionNeeded("");
    }
  };

  const handleStep2Response = (value: string) => {
    setRevisionNeeded(value);
  };

  const saveM9Responses = async () => {
    // Save M-9 responses to SharePoint
    if (!companyName) {
      setSaveError("Company name not available");
      setSaveStatus('error');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const responses: Record<string, any> = {};

      // M-9: Store both user responses (first question and sub-question)
      if (changesObserved) {
        responses['M-9'] = {
          questionId: 'M-9',
          selectedOption: changesObserved,
          subAnswer: revisionNeeded || null, // Store the second question answer
          textResponse: null,
          documents: [],
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // Save to SharePoint
      const planningAPI = (window as any).planningAPI;
      if (planningAPI?.saveAllResponses) {
        const saveResult = await planningAPI.saveAllResponses({
          companyName,
          sectionKey: "materiality",
          responses
        });

        if (saveResult && saveResult.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(saveResult?.error || "Failed to save to SharePoint");
        }
      } else {
        throw new Error("Planning API not available");
      }
    } catch (error: any) {
      console.error("Error saving M-9 responses:", error);
      setSaveError(error.message || "Failed to save responses");
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleViewRevisedMateriality = () => {
    // Initialize revision data with planning benchmarks if available
    if (planningData && planningData.benchmarkData && planningData.benchmarkData.length > 0) {
      const initialRevisionData = planningData.benchmarkData.map((item: any) => ({
        ...item,
        amount: 0, // Clear amounts for re-entry
        adjustments: 0, // Clear adjustments for re-entry
      }));

      setRevisionData((prev) => ({
        ...prev,
        benchmarkData: initialRevisionData,
        auditTrail: {
          ...prev.auditTrail,
          oldMateriality: planningData.determinedMateriality || 0,
        },
      }));
    } else {
      // If no planning data, initialize with empty benchmark data
      setRevisionData((prev) => ({
        ...prev,
        benchmarkData: [],
        auditTrail: {
          ...prev.auditTrail,
          oldMateriality: 0,
        },
      }));
    }

    setStep(3);
  };

  const updateRevisionBenchmarkData = (
    index: number,
    field: string,
    value: number,
  ) => {
    setRevisionData((prev) => {
      const newData = [...prev.benchmarkData];
      newData[index] = { ...newData[index], [field]: value };
      return { ...prev, benchmarkData: newData };
    });
  };

  const calculateRevisedMateriality = () => {
    // Calculate materiality for each benchmark and take the minimum
    const materialities = revisionData.benchmarkData.map((item) => {
      const normalizedAmount = item.amount + item.adjustments;
      return (normalizedAmount * item.factor) / 100;
    });

    // Find the minimum materiality value (only if there are benchmarks with positive values)
    const positiveMaterialities = materialities.filter(m => m > 0);
    const minimum = positiveMaterialities.length > 0 
      ? Math.min(...positiveMaterialities)
      : 0;

    setRevisionData((prev) => ({
      ...prev,
      determinedMateriality: minimum,
      auditTrail: {
        ...prev.auditTrail,
        newMateriality: minimum,
      },
    }));
  };

  const saveRevision = async () => {
    // Save to SharePoint (no redirect - stays on revision tab)
    if (!companyName) {
      setSaveError("Company name not available");
      setSaveStatus('error');
      return;
    }

    // Trigger auto-save for DocumentManager before saving
    try {
    } catch (error) {
      console.warn('Failed to trigger auto-save for DocumentManager:', error);
    }

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const responses: Record<string, any> = {};

      // M-9: Store both user responses (first question and sub-question)
      if (changesObserved) {
        responses['M-9'] = {
          questionId: 'M-9',
          selectedOption: changesObserved,
          subAnswer: revisionNeeded || null, // Store the second question answer
          textResponse: null,
          documents: [],
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // M-15: Justification for Revision (document type)
      const hasM15Documents = m15Documents && Array.isArray(m15Documents) && m15Documents.length > 0;
      const hasM15TextResponse = m15TextResponse && (
        (m15TextResponse.html && m15TextResponse.html.trim().length > 0) || 
        (m15TextResponse.plainText && m15TextResponse.plainText.trim().length > 0)
      );
      
      // Always save if we have ANY data (documents or textResponse)
      if (hasM15Documents || hasM15TextResponse) {
        // Build documents array (excluding textbox type)
        const fileDocuments = hasM15Documents 
          ? m15Documents.filter((doc: any) => doc.type !== 'textbox').map((doc: any) => ({
              name: doc.name,
              type: doc.type,
              url: doc.url,
              reference: doc.reference
            }))
          : [];
        
        // Build textResponse - only from state (from SharePoint)
        const m15TextResponseData = hasM15TextResponse ? m15TextResponse : null;
        
        responses['M-15'] = {
          questionId: 'M-15',
          selectedOption: null,
          textResponse: m15TextResponseData,
          documents: fileDocuments,
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // Save revision table in the same format as M-2 (Planning Materiality)
      if (revisionData.benchmarkData && revisionData.benchmarkData.length > 0) {
        responses['revisedMaterialityTable'] = {
          questionId: 'revisedMaterialityTable',
          selectedOption: revisionData.benchmarkData.map(item => item.benchmark),
          textResponse: null,
          documents: [],
          table: {
            columns: [
              { key: "benchmark", label: "Benchmark" },
              { key: "amount", label: "Amount (A)" },
              { key: "normalizationAdjustments", label: "Normalization Adjustments (B)" },
              { key: "normalizedAmount", label: "Normalized Amount (C=A+B)" },
              { key: "benchmarkFactor", label: "Benchmark Factor (%)" },
              { key: "materiality", label: "Materiality (C×%)" }
            ],
            rows: revisionData.benchmarkData.map(item => {
              const normalizedAmount = item.amount + item.adjustments;
              const materiality = (normalizedAmount * item.factor) / 100;
              return {
                benchmark: item.benchmark,
                amount: item.amount,
                normalizationAdjustments: item.adjustments,
                normalizedAmount: normalizedAmount,
                benchmarkFactor: item.factor,
                materiality: materiality
              };
            })
          },
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // Save Revised Materiality, Performance Materiality, and Clearly Trivial Threshold
      if (revisionData.determinedMateriality > 0 || step === 3) {
        const performancePercentage = planningData?.performancePercentage || 75;
        const trivialPercentage = planningData?.trivialPercentage || 5;
        
        const performanceMateriality =
          (revisionData.determinedMateriality * performancePercentage) / 100;

        const trivialThreshold =
          (revisionData.determinedMateriality * trivialPercentage) / 100;

        responses['revisedMateriality'] = {
          determinedMateriality: revisionData.determinedMateriality || 0,
          performanceMateriality: performanceMateriality,
          trivialThreshold: trivialThreshold,
          auditTrail: revisionData.auditTrail,
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // Save to SharePoint
      const planningAPI = (window as any).planningAPI;
      if (planningAPI?.saveAllResponses) {
        const saveResult = await planningAPI.saveAllResponses({
          companyName,
          sectionKey: "materiality",
          responses
        });

        if (saveResult && saveResult.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(saveResult?.error || "Failed to save to SharePoint");
        }
      } else {
        throw new Error("Planning API not available");
      }
    } catch (error: any) {
      console.error("Error saving Materiality Revision:", error);
      setSaveError(error.message || "Failed to save revision");
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Debug log for tracking cross-section answers (for future conditional logic)
  console.log("MaterialityRevision crossSectionAnswers:", crossSectionAnswers);

  // Landing page - always show both questions
  if (step === 1) {
    const bothYes = changesObserved === "Yes" && revisionNeeded === "Yes";
    
    return (
      <div className="space-y-6">
        {/* Header with Review Comment Button */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Materiality Revision</h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setReviewCommentOpen(true)}
            className="text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
            title="Review Comment"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Review Comment
          </Button>
        </div>
        
        {/* Row-wise layout for questions */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Question 1 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Step 1: Changes Assessment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm">
                Have we come across any instances of changes in circumstances,
                new information, or changes in auditor's understanding of the
                entity and its operations as a result of performing further
                audit procedures?
              </p>

              <RadioGroup
                value={changesObserved}
                onValueChange={handleStep1Response}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Yes" id="changes-yes" />
                  <Label htmlFor="changes-yes">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="No" id="changes-no" />
                  <Label htmlFor="changes-no">No</Label>
                </div>
              </RadioGroup>

              {changesObserved === "No" && (
                <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-green-800">
                  <span className="text-sm">
                    No revision required. Process completed - no changes
                    observed during audit procedures.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Question 2 */}
          <Card className={changesObserved !== "Yes" ? "opacity-50" : ""}>
            <CardHeader>
              <CardTitle className="text-base">
                Step 2: Revision Assessment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm">
                Does the materiality determined at planning stage need revision?
              </p>

              <RadioGroup
                value={revisionNeeded}
                onValueChange={handleStep2Response}
                disabled={changesObserved !== "Yes"}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="Yes"
                    id="revision-yes"
                    disabled={changesObserved !== "Yes"}
                  />
                  <Label htmlFor="revision-yes">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="No"
                    id="revision-no"
                    disabled={changesObserved !== "Yes"}
                  />
                  <Label htmlFor="revision-no">No</Label>
                </div>
              </RadioGroup>

              {revisionNeeded === "No" && changesObserved === "Yes" && (
                <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-green-800">
                  <span className="text-sm">
                    Change observed but no revision required. Process completed.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Save and View buttons */}
        <div className="flex justify-end items-center gap-3 pt-4">
          {saveStatus === 'saved' && (
            <Badge variant="outline" className="border-green-600 text-green-600 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Saved successfully
            </Badge>
          )}
          {saveStatus === 'error' && (
            <Badge variant="outline" className="border-red-600 text-red-600 flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              {saveError || "Error saving"}
            </Badge>
          )}
          {(changesObserved || revisionNeeded) && (
            <Button
              onClick={saveM9Responses}
              className="flex items-center gap-2"
              disabled={isArchivalMode || saving || !companyName}
              variant="outline"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Responses
                </>
              )}
            </Button>
          )}
          {/* Show "View Revised Materiality" button only when both questions are "Yes" */}
          {bothYes && (
            <Button
              onClick={handleViewRevisedMateriality}
              className="flex items-center gap-2"
              data-allow-archival-edit={true}
            >
              <Calculator className="h-4 w-4" />
              View Revised Materiality
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (step === 3) {
    // Calculate using the same percentages as saved in planning materiality
    // Use default percentages if planning data is not available
    const performancePercentage = planningData?.performancePercentage || 75;
    const trivialPercentage = planningData?.trivialPercentage || 5;
    
    const performanceMateriality =
      (revisionData.determinedMateriality * performancePercentage) / 100;

    const trivialThreshold =
      (revisionData.determinedMateriality * trivialPercentage) / 100;

    return (
      <div className="space-y-8">
        {/* Header with Review Comment Button */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Materiality Revision</h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setReviewCommentOpen(true)}
            className="text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
            title="Review Comment"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Review Comment
          </Button>
        </div>
        
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep(1)}
            className="flex items-center gap-2"
            disabled={isArchivalMode}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>

          <Button
            onClick={calculateRevisedMateriality}
            className="flex items-center gap-2"
            disabled={isArchivalMode}
          >
            <Calculator className="h-4 w-4" />
            Calculate Revised Materiality
          </Button>
        </div>

        <div className="space-y-6">
          <h3 className="text-lg font-semibold">Materiality Revision Form</h3>

          {/* Benchmarks from Planning Stage (Read-only) */}
          {hasPlanningData && planningData.selectedBenchmarks && planningData.selectedBenchmarks.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Benchmarks from Planning Stage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {planningData.selectedBenchmarks.map(
                    (benchmark: string, index: number) => {
                      const planningBenchmark = planningData.benchmarkData?.find(
                        (b: any) => b.benchmark === benchmark,
                      );
                      return (
                        <div
                          key={benchmark}
                          className="bg-muted flex items-center justify-between rounded p-2"
                        >
                          <span className="font-medium">{benchmark}</span>
                          <Badge variant="outline">
                            {planningBenchmark?.factor || 5}%
                          </Badge>
                        </div>
                      );
                    },
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {!hasPlanningData && (
            <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-4 text-blue-800">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">
                Planning data is not yet available. You can still proceed with revision, but some features may be limited.
              </span>
            </div>
          )}

          {/* Revision Data Entry */}
          <div className="space-y-4">
            <h4 className="font-medium">Updated Financial Data</h4>
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benchmark</TableHead>
                    <TableHead>Amount (A)</TableHead>
                    <TableHead>Normalization Adjustments (B)</TableHead>
                    <TableHead>Normalized Amount (C=A+B)</TableHead>
                    <TableHead>Benchmark Factor (%)</TableHead>
                    <TableHead>Materiality (C×%)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revisionData.benchmarkData.map((item, index) => {
                    const normalizedAmount = item.amount + item.adjustments;
                    const materiality = (normalizedAmount * item.factor) / 100;

                    return (
                      <TableRow key={item.benchmark}>
                        <TableCell className="font-medium">
                          {item.benchmark}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={item.amount || ""}
                            onChange={(e) =>
                              updateRevisionBenchmarkData(
                                index,
                                "amount",
                                Number.parseFloat(e.target.value) || 0,
                              )
                            }
                            className="w-24"
                            placeholder="Enter new amount"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={item.adjustments || ""}
                            onChange={(e) =>
                              updateRevisionBenchmarkData(
                                index,
                                "adjustments",
                                Number.parseFloat(e.target.value) || 0,
                              )
                            }
                            className="w-24"
                            placeholder="Enter adjustments"
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {normalizedAmount.toLocaleString('en-IN')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.factor}%</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {materiality.toLocaleString('en-IN')}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Calculated Values */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Revised Materiality</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="number"
                  value={revisionData.determinedMateriality || ""}
                  onChange={(e) =>
                    setRevisionData((prev) => ({
                      ...prev,
                      determinedMateriality: Number.parseFloat(e.target.value) || 0,
                      auditTrail: {
                        ...prev.auditTrail,
                        newMateriality: Number.parseFloat(e.target.value) || 0,
                      },
                    }))
                  }
                  placeholder="Enter revised materiality"
                  className="text-lg font-semibold"
                  step="0.01"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  Performance Materiality
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="secondary" className="px-4 py-2 text-lg">
                  {performanceMateriality.toLocaleString('en-IN')}
                </Badge>
                <p className="text-muted-foreground mt-1 text-xs">
                  {performancePercentage}% of materiality
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  Clearly Trivial Threshold
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="secondary" className="px-4 py-2 text-lg">
                  {trivialThreshold.toLocaleString('en-IN')}
                </Badge>
                <p className="text-muted-foreground mt-1 text-xs">
                  {trivialPercentage}% of materiality
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Comparison */}
          {revisionData.determinedMateriality > 0 && hasPlanningData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Materiality Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-sm">
                      Planning Stage
                    </Label>
                    <div className="text-lg font-semibold">
                      {planningData.determinedMateriality.toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">
                      Revised
                    </Label>
                    <div className="text-lg font-semibold">
                      {revisionData.determinedMateriality.toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <Label className="text-muted-foreground text-sm">
                    Change
                  </Label>
                  <div
                    className={`text-lg font-semibold ${
                      revisionData.determinedMateriality >
                      planningData.determinedMateriality
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {revisionData.determinedMateriality >
                    planningData.determinedMateriality
                      ? "+"
                      : ""}
                    {(
                      revisionData.determinedMateriality -
                      planningData.determinedMateriality
                    ).toLocaleString('en-IN')}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Justification */}
          <div className="space-y-2">
            <Label>Justification for Revision</Label>
            <DocumentManager
              sectionKey="materiality"
              groupKey="Revised Materiality"
              itemId="M-15"
              itemQuestion="Provide justification for the materiality revision."
              itemTextResponse={m15TextResponse}
              itemDocuments={m15Documents}
              onDocumentsChange={setM15Documents}
              onTextResponseChange={setM15TextResponse}
            />
          </div>

          <div className="flex justify-end items-center gap-3">
            {saveStatus === 'saved' && (
              <Badge variant="outline" className="border-green-600 text-green-600 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Saved successfully
              </Badge>
            )}
            {saveStatus === 'error' && (
              <Badge variant="outline" className="border-red-600 text-red-600 flex items-center gap-1">
                <XCircle className="h-3 w-3" />
                {saveError || "Error saving"}
              </Badge>
            )}
            <Button 
              onClick={saveRevision} 
              className="flex items-center gap-2"
              disabled={isArchivalMode || saving || !companyName}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Revision
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Review Comment Modal */}
        <ReviewComment
          questionId="Materiality-Revision"
          questionText="Materiality Revision"
          isOpen={reviewCommentOpen}
          onClose={() => setReviewCommentOpen(false)}
          workspace="Materiality"
          subPhase="Materiality Revision"
        />
      </div>
    );
  }

  return null;
}
